import { AgGridReact } from 'ag-grid-react'
import { useCallback, useMemo } from 'react'
import 'ag-grid-enterprise'
import { absencesByPersonQuery, longAbsenceTypesQuery } from '@hr/api/index.js'
import {
	translatedCausecode,
	useGetAbscenceTypesOptions,
} from '@hr/mgmtview/personalprofile/newabscencemodal/newabscencemodal.constants.js'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import Chip from '../../../../common/chip/Chip.jsx'
import { defaultColDef, gridSettings } from '../../../../common/grid'
import LoadingCentered from '../../../../common/loaders/loadingcentered'
import { dateFormatter, dateGetter } from '../../../../common/utils'
import { AddLongAbsenceRow } from './addlongabsencerow'

export function PersonAbsences({ person, isPersonal }) {
	const { t } = useTranslation()

	const { data: personEventsResponse, isLoading, error } = useQuery(absencesByPersonQuery(person?.personId))
	const { data: longAbsenceTypes = [] } = useQuery(longAbsenceTypesQuery)
	const { options: translatedOptions } = useGetAbscenceTypesOptions()

	const eventLabelMap = useMemo(() => {
		const map = {}

		if (translatedOptions) {
			translatedOptions.forEach((category) => {
				category.options?.forEach((option) => {
					map[option.value] = option.label
				})
			})
		}

		if (longAbsenceTypes) {
			longAbsenceTypes.forEach((type) => {
				let cleanName = ''
				if (type.causeCode === 'HOURS') {
					cleanName = t('Hours2', 'Tunnit')
				} else if (type.causeCode === 'FLEXI') {
					cleanName = t('FLEXI', 'Saldovapaa')
				} else {
					cleanName = translatedCausecode(type.causeCode, t)
				}

				const paymentStatus = type.paid ? t('Paid', 'palkallinen') : t('Unpaid', 'palkaton')

				map[type.causeCode] = `${cleanName} (${paymentStatus})`
			})
		}

		return map
	}, [translatedOptions, longAbsenceTypes, t])

	const statusOptions = useMemo(
		() => [
			{ value: 'OPEN', label: t('Open', 'Avoinna') },
			{ value: 'APPROVED', label: t('Approved', 'Hyväksytty') },
			{ value: 'REJECTED', label: t('Rejected', 'Hylätty') },
			{ value: 'DONE', label: t('Done', 'Valmis') },
		],
		[t],
	)

	const personEvents = useMemo(() => {
		if (!personEventsResponse) return []
		const rawEvents = Array.isArray(personEventsResponse) ? personEventsResponse : personEventsResponse?.data || []

		return rawEvents
			.filter((e) => e.causeCode !== 'HOURS' && e.causeCode !== 'FLEXI')
			.map((e) => ({
				...e,
				startDate: e.startDate ? new Date(e.startDate) : '',
				endDate: e.endDate ? new Date(e.endDate) : '',
				status: e.status || 'OPEN',
				longAbsence: !!e.longAbsence,
			}))
	}, [personEventsResponse])

	const absenceTypeRenderer = useCallback(
		(params) => {
			if (!params?.data?.causeCode) {
				return ''
			}

			if (params.data.longAbsence) {
				return (
					<Chip style={{ backgroundColor: '#C2660A', color: '#ffffff' }} variant="warning">
						{t('LongAbsence', 'Pitkä poissaolo')}
					</Chip>
				)
			}

			return (
				<Chip style={{ backgroundColor: '#1489B8', color: '#ffffff' }} variant="info">
					{t('Absence', 'Poissaolo')}
				</Chip>
			)
		},
		[t],
	)

	const columnDefs = useMemo(
		() => [
			{
				headerName: t('Event', 'Tapahtuma'),
				field: 'causeCode',
				valueGetter: (params) => {
					if (!params?.data?.causeCode) return ''
					return eventLabelMap[params.data.causeCode] || params.data.causeCode
				},
				filter: true,
			},
			{
				headerName: t('Type', 'Tyyppi'),
				field: 'longAbsence',
				cellRenderer: absenceTypeRenderer,
				filter: true,
				width: 150,
			},
			{
				headerName: t('Status', 'Tila'),
				field: 'status',
				filter: 'agSetColumnFilter',
				filterParams: {
					values: statusOptions.map((opt) => opt.value),
					valueFormatter: (params) => {
						const option = statusOptions.find((o) => o.value === params.value)
						return option ? option.label : params.value || ''
					},
				},
				valueFormatter: (params) => {
					const option = statusOptions.find((o) => o.value === params.value)
					return option ? option.label : params.value || ''
				},
				hide: true,
			},
			{
				headerName: t('AbsenceStartDate', 'Alkupäivämäärä'),
				cellClass: 'dateType',
				field: 'startDate',
				filter: 'agDateColumnFilter',
				valueGetter: dateGetter,
				valueFormatter: dateFormatter,
			},
			{
				headerName: t('EndDate', 'Päättymispäivämäärä'),
				cellClass: 'dateType',
				field: 'endDate',
				filter: 'agDateColumnFilter',
				valueGetter: dateGetter,
				valueFormatter: dateFormatter,
			},
			{
				headerName: t('Info', 'Info'),
				field: 'info',
				filter: true,
				flex: 1,
			},
		],
		[t, eventLabelMap, statusOptions, absenceTypeRenderer],
	)

	if (isLoading) return <LoadingCentered show />
	if (error) return <div className="text-danger text-center p-3">{error.message}</div>

	return (
		<>
			<div className="ag-theme-material styles-ag w-100" style={{ height: 450, marginTop: '1rem' }}>
				<AgGridReact
					{...gridSettings}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					rowData={personEvents}
					animateRows
					enableCellTextSelection
					getRowId={(params) => String(params.data.id || Math.random())}
					suppressContextMenu={true}
				/>
			</div>

			{!isPersonal && (
				<>
					<h5 className="mt-4 mb-2">{t('AddLongAbsence', 'Lisää pitkä poissaolo')}</h5>
					{person?.personId && <AddLongAbsenceRow person={person} />}
				</>
			)}
		</>
	)
}
